import { ASSET_CLASS_IDS } from "~/consts";
import { AssetColumns } from "~/modules/assets/types";
import {
    ApiQuery,
    Filter,
    FilterOperators,
    SortDirections,
} from "~/modules/filters/types";

// // // //

/**
 * NEW_ASSET_MONITOR_PAGE_SIZE
 * The number of assets shown per page in the New Asset Monitor table
 */
export const NEW_ASSET_MONITOR_PAGE_SIZE = 25;

/**
 * NEW_ASSET_MONITOR_FILTER
 * Defines which assets are surfaced by the New Asset Monitor. This is the
 * single source of truth for that definition - the homepage's
 * "Recently Registered Assets" card (which links to the monitor via
 * "View All") applies the same filter so both surfaces always agree on which
 * assets are shown.
 */
export const NEW_ASSET_MONITOR_FILTER: Filter = {
    operator: FilterOperators.And,
    filters: [
        {
            field: AssetColumns.tokenCount,
            operator: FilterOperators.GreaterThan,
            value: 0,
        },
        {
            field: AssetColumns.primaryMarketID,
            operator: FilterOperators.GreaterThan,
            value: 0,
        },
        {
            field: AssetColumns.isInvestable,
            operator: FilterOperators.Equals,
            value: true,
        },
        {
            field: AssetColumns.inceptionDate,
            operator: FilterOperators.IsNotNull,
            value: null,
        },
        {
            field: AssetColumns.assetClassID,
            operator: FilterOperators.NotEquals,
            value: ASSET_CLASS_IDS.Cryptocurrency,
        },
        {
            field: AssetColumns.assetClassID,
            operator: FilterOperators.NotEquals,
            value: ASSET_CLASS_IDS.Stablecoin,
        },
    ],
};

export const NEW_ASSET_MONITOR_FIELDS: string[] = [
    "id",
    "name",
    "ticker",
    "icon_url",
    "asset_class",
    "asset_class_id",
    "asset_class_name",
    "inception_date",
    "circulating_asset_value_dollar.val",
    "_created_at",
    "asset_class",
    "issuer",
    "issuer_name",
    "manager",
    "primary_market",
    "primary_markets",
    "yield_to_maturity_percent",
    "apy_30_day",
    "total_asset_value_dollar",
    "tokens.id",
    "tokens.name",
    "tokens.address",
    "tokens.standards",
    "tokens.platform",
    "tokens.network",
    "tokens.asset_manager",
];

/**
 * NEW_ASSET_MONITOR_QUERY
 * The default query for the New Asset Monitor table (Registration Date, newest
 * first). Used to statically generate the first page of results
 * (src/pages/new-asset-monitor) and as the table's default ApiQuery - every
 * other page / sort is fetched client-side.
 */
export const NEW_ASSET_MONITOR_QUERY: ApiQuery = {
    name: "new-asset-monitor",
    // NOTE - NEW_ASSET_MONITOR_FILTER is nested to keep it separate from the
    // filters applied by the user. The filter UI looks up / adds / removes its
    // filters by `field` at the top level, so a user's Asset Class filter would
    // otherwise collide with the asset_class_id conditions defined above.
    filter: {
        operator: FilterOperators.And,
        filters: [NEW_ASSET_MONITOR_FILTER],
    },
    fields: NEW_ASSET_MONITOR_FIELDS,
    sort: {
        field: AssetColumns._created_at,
        direction: SortDirections.desc,
    },
    pagination: {
        page: 1,
        perPage: NEW_ASSET_MONITOR_PAGE_SIZE,
    },
};
